import { IRegionByDomain } from '../../types/geo';
import { getBuilderSuggests } from '../../api/get_builder_suggests';
import { getCachedGeocode } from '../../api/get_cached_geocode';
import { getRegionById } from '../../api/get_region_by_id';
import { getRegionCities } from '../../api/get_region_cities';
import { getVillageSuggests } from '../../api/get_village_suggests';
import { searchRegionsCities } from '../../api/search_regions_cities';
import {
  DEFAULT_MAX_BUILDER_SUGGESTIONS,
  DEFAULT_MAX_VILLAGE_SUGGESTIONS,
  DEFAULT_REGION_CITIES_MAP,
  MAX_VILLAGE_SUGGESTIONS,
} from '../../interfaces/villages';
import { IThunkActionCreator } from '../../types/store';
import {
  builderSuggestionsFetched,
  builderSuggestionsFetchError,
  geoCachedError,
  geoCachedFetched,
  geoLoadingStarted,
  regionCitiesFetched,
  regionCitiesFetchError,
  regionCitiesFetchStarted,
  regionFetched,
  regionFetchError,
  regionsCitiesFetched,
  regionsCitiesFetchError,
  regionsCitiesFetchStarted,
  regionsFetched,
  villageSuggestionsFetched,
  villageSuggestionsFetchError,
} from './actions';
import { ICity } from './';
import { getRegions } from '../../api/get_regions';
import { fetchGeocodedSearchData } from './fetchGeocodedSearchData';
import { IApiGeoGeocodeCachedRequest } from '../../repositories/monolith-cian-realty/unversioned/api-geo-geocode-cached';

export function fetchCachedGeocode(parameters: IApiGeoGeocodeCachedRequest): IThunkActionCreator {
  return async (dispatch, getState, context) => {
    dispatch(geoLoadingStarted());

    try {
      const response = await getCachedGeocode(context.httpApi, parameters, context.subdomain);
      const responseItems = response.items || [];

      dispatch(geoCachedFetched(responseItems));

      const firstOccurence = responseItems.length && responseItems[0];
      if (!firstOccurence) {
        return;
      }

      dispatch(
        fetchGeocodedSearchData({
          address: firstOccurence.text,
          kind: firstOccurence.kind,
          lat: (firstOccurence.coordinates as number[])[1],
          lng: (firstOccurence.coordinates as number[])[0],
        }),
      );
    } catch (error) {
      dispatch(geoCachedError(error.message));
    }
  };
}

export function fetchRegionCities(regionId: number): IThunkActionCreator {
  return async (dispatch, getState, context) => {
    dispatch(regionCitiesFetchStarted());
    const defaultRegions = DEFAULT_REGION_CITIES_MAP[regionId];
    if (defaultRegions) {
      dispatch(regionCitiesFetched(defaultRegions));

      return;
    }

    try {
      const response = await getRegionCities(
        context.httpApi,
        {
          regionId,
        },
        context.subdomain,
      );

      const cities: ICity[] = Array.isArray(response.data.items)
        ? response.data.items.map(city => ({
            displayName: city.displayName,
            fullName: city.fullName,
            id: city.id,
            mainTownId: city.mainTownId,
            regionId: city.parentId,
          }))
        : [];
      dispatch(regionCitiesFetched(cities));
    } catch (e) {
      dispatch(regionCitiesFetchError());
    }
  };
}

export function fetchRegionsCities(text: string): IThunkActionCreator {
  return (dispatch, getState, context) => {
    if (!text) {
      return dispatch(regionsCitiesFetched([]));
    }

    dispatch(regionsCitiesFetchStarted());

    return searchRegionsCities(context.httpApi, { text }, context.subdomain)
      .then(response => {
        dispatch(regionsCitiesFetched(response.data.items || []));
      })
      .catch(() => {
        dispatch(regionsCitiesFetchError());
      });
  };
}

export function fetchRegion(): IThunkActionCreator {
  return async (dispatch, getState, context) => {
    const { regionId } = getState().filters;

    try {
      const response = await getRegionById(context.httpApi, { regionId: regionId || 1 });
      dispatch(regionFetched(response.data as IRegionByDomain));
    } catch (error) {
      dispatch(regionFetchError());
    }
  };
}

export const clearVillageSuggestions = (): IThunkActionCreator => dispatch => dispatch(villageSuggestionsFetchError());

export const clearBuilderSuggestions = (): IThunkActionCreator => dispatch => dispatch(builderSuggestionsFetchError());

export function fetchVillageSuggestions(name: string): IThunkActionCreator {
  return async (dispatch, getState, context) => {
    const { regionId } = getState().filters;
    const kpOnly = name.toLowerCase().startsWith('кп');

    if (!name) {
      dispatch(clearVillageSuggestions());
    }

    try {
      const suggestions = await getVillageSuggests(context.httpApi, {
        name: name.replace(/^КП\s*/i, ''),
        regionId: regionId || 1,
      });

      dispatch(
        villageSuggestionsFetched(
          suggestions.slice(0, kpOnly ? MAX_VILLAGE_SUGGESTIONS : DEFAULT_MAX_VILLAGE_SUGGESTIONS),
        ),
      );
    } catch (error) {
      dispatch(clearVillageSuggestions());
    }
  };
}

export function fetchBuilderSuggestions(name: string): IThunkActionCreator {
  return async (dispatch, getState, context) => {
    const { regionId = 1 } = getState().filters;

    if (!name) {
      dispatch(clearBuilderSuggestions());
    }

    try {
      const suggests = await getBuilderSuggests(context.httpApi, { name, regionId });

      dispatch(builderSuggestionsFetched(suggests.slice(0, DEFAULT_MAX_BUILDER_SUGGESTIONS)));
    } catch (error) {
      dispatch(clearBuilderSuggestions());
    }
  };
}

export const fetchRegions =
  (): IThunkActionCreator =>
  async (dispatch, getState, { httpApi, logger }) => {
    const {
      geo: { regions },
    } = getState();
    if (!regions.length) {
      const {
        data: { items },
      } = await getRegions(httpApi, logger);
      if (!Array.isArray(items)) {
        return;
      }

      dispatch(regionsFetched(items));
    }
  };
